import {
  CFormInput,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CSpinner,
} from "@coreui/react";
import { FormEvent, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useCookie } from "../../hooks/useCookie";
import { useToast } from "../../hooks/useToast";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";

const generatePassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  return Array.from(
    { length: 10 },
    () => chars[Math.floor(Math.random() * chars.length)],
  ).join("");
};

interface Props {
  request: PartnerRequest | null;
  open: boolean;
  onClose: () => void;
}

export default function CreatePartnerAccountModal({
  request,
  open,
  onClose,
}: Props) {
  const { token } = useCookie("vToken");
  const { toastSuccess, toastError } = useToast();
  const queryClient = useQueryClient();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [createdCredentials, setCreatedCredentials] = useState<{
    email: string;
    password: string;
  } | null>(null);
  const initializedRequestId = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      initializedRequestId.current = null;
      return;
    }
    // A successful create refreshes the request object. Re-initializing on
    // that new object wiped the credentials screen and showed the form again.
    if (!request || initializedRequestId.current === request.id) return;

    initializedRequestId.current = request.id;
    setFullName(request.fullName ?? "");
    setEmail(request.email ?? "");
    setPhone(request.phone ?? "");
    setPassword(generatePassword());
    setCreatedCredentials(null);
  }, [open, request]);

  const createPartner = useMutation((payload: Record<string, unknown>) => {
    return apiService.MakePostRequest("users/partner", payload, token);
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!request) return;

    createPartner.mutate(
      {
        fullName,
        email,
        phone,
        password,
        acceptedTerms: true,
        cityId: request.city?.id ? Number(request.city.id) : undefined,
        partnerRequestId: request.id,
      },
      {
        onSuccess() {
          setCreatedCredentials({ email, password });
          queryClient.invalidateQueries("getPartnersRequest");
          toastSuccess("Compte partenaire créé");
        },
        onError(error: any) {
          toastError(
            error?.response?.data?.message ||
              general.fr.message.operationFailed,
          );
        },
      },
    );
  };

  const copyCredentials = async () => {
    if (!createdCredentials) return;
    await navigator.clipboard.writeText(
      `Email: ${createdCredentials.email}\nMot de passe: ${createdCredentials.password}`,
    );
    toastSuccess("Identifiants copiés");
  };

  return (
    <CModal
      alignment="center"
      visible={open}
      backdrop="static"
      onClose={onClose}
    >
      <CModalHeader>
        <CModalTitle>Créer un compte partenaire</CModalTitle>
      </CModalHeader>
      <CModalBody>
        {createdCredentials ? (
          <div>
            <p className="mb-3">
              Compte créé. Envoyez ces identifiants au partenaire :
            </p>
            <div className="bg-light rounded p-3 mb-3">
              <p className="mb-1">
                <strong>Email :</strong> {createdCredentials.email}
              </p>
              <p className="mb-0">
                <strong>Mot de passe :</strong> {createdCredentials.password}
              </p>
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={copyCredentials}
              >
                Copier
              </button>
              <button
                type="button"
                className="btn btn-primary text-white"
                onClick={onClose}
              >
                Fermer
              </button>
            </div>
          </div>
        ) : (
          <form className="row" onSubmit={handleSubmit}>
            <div className="col-12 mb-3">
              <label htmlFor="partnerFullName" className="d-block">
                Nom et prénom
              </label>
              <CFormInput
                id="partnerFullName"
                className="custom-input"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </div>
            <div className="col-12 mb-3">
              <label htmlFor="partnerEmail" className="d-block">
                Email
              </label>
              <CFormInput
                id="partnerEmail"
                type="email"
                className="custom-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="col-12 mb-3">
              <label htmlFor="partnerPhone" className="d-block">
                Téléphone
              </label>
              <CFormInput
                id="partnerPhone"
                className="custom-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </div>
            <div className="col-12 mb-3">
              <label className="d-block">Ville</label>
              <CFormInput
                className="custom-input"
                value={request?.city?.label ?? ""}
                readOnly
              />
            </div>
            <div className="col-12 mb-3">
              <label htmlFor="partnerPassword" className="d-block">
                Mot de passe
              </label>
              <CFormInput
                id="partnerPassword"
                className="custom-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            <div className="d-flex justify-content-end gap-2 mt-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onClose}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="btn btn-primary text-white"
                disabled={createPartner.isLoading}
              >
                {createPartner.isLoading ? (
                  <CSpinner size="sm" />
                ) : (
                  "Créer le compte"
                )}
              </button>
            </div>
          </form>
        )}
      </CModalBody>
    </CModal>
  );
}
